import React from "react";
import { Table } from "reactstrap";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import Api from "services/api";
import { formatServerDateTime } from "helpers/formatServerDateTime";
import FullPageLoader from "components/common/FullPageLoader";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import Pagination from "components/Pagination";
import NotificationLogDetailModal from "components/common/NotificationLogDetailModal";

/** Global outgoing-email log — who was emailed what, when, with a full preview. */
const NotificationLogs = () => {
  const [appointmentId, setAppointmentId] = React.useState("");
  const [recipient, setRecipient] = React.useState("");
  const [subject, setSubject] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [meta, setMeta] = React.useState({ total: 0, last_page: 1, current_page: 1, per_page: 25 });
  const [loading, setLoading] = React.useState(false);
  const [detailLogId, setDetailLogId] = React.useState(null);

  const params = (page) => ({
    appointment_id: appointmentId.trim(),
    recipient: recipient.trim(),
    subject: subject.trim(),
    start_date: startDate,
    end_date: endDate,
    pageLength: 25,
    page,
  });

  const fetchLogs = async (page = 1, override) => {
    setLoading(true);
    const res = await Api.fetchNotificationLogs(page, override ?? params(page));
    setLoading(false);
    if (res?.status >= 200 && res?.status < 300) {
      const data = res?.data?.data;
      setRows(data?.data || []);
      setMeta({
        total: data?.total || 0,
        last_page: data?.last_page || 1,
        current_page: data?.current_page || 1,
        per_page: data?.per_page || 25,
      });
    } else {
      toast.error(res?.data?.message || "Could not load notification logs.", { className: "errorToast" });
    }
  };

  React.useEffect(() => {
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    setAppointmentId("");
    setRecipient("");
    setSubject("");
    setStartDate("");
    setEndDate("");
    fetchLogs(1, { pageLength: 25, page: 1 });
  };

  return (
    <div className="content">
      {loading && <FullPageLoader />}
      <div className="row_main_head">
        <h2 className="main_heading">Notification Logs</h2>
      </div>

      <div className="card_table p-4">
        <div className="row-primary row-r" style={{ alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
          <div className="row-primary">
            <h2>Job ID</h2>
            <input className="input-in type_inp" type="text" placeholder="e.g. A222905" value={appointmentId} onChange={(e) => setAppointmentId(e.target.value)} />
          </div>
          <div className="row-primary">
            <h2>Recipient</h2>
            <input className="input-in type_inp" type="text" placeholder="email contains…" value={recipient} onChange={(e) => setRecipient(e.target.value)} />
          </div>
          <div className="row-primary">
            <h2>Subject</h2>
            <input className="input-in type_inp" type="text" placeholder="subject contains…" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="date row-primary">
            <h2>Start Date</h2>
            <div className="input-image">
              <input type="date" className="input-in type_inp" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
          </div>
          <div className="date row-primary">
            <h2>End Date</h2>
            <div className="input-image">
              <input type="date" className="input-in type_inp" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <SubmitButton buttonType="button" btnText="Search" className="green" handleSubmit={() => fetchLogs(1)} />
            <SubmitButton buttonType="button" btnText="Reset" className="lightBlue" handleSubmit={handleReset} />
          </div>
        </div>
      </div>

      <div className="card_table p-4">
        <p style={{ color: "#809FB8", marginBottom: 8 }}>{meta.total} record{meta.total === 1 ? "" : "s"}</p>
        <div className="table_responsive">
          <Table>
            <thead className="text-primary">
              <tr>
                <th>#</th>
                <th>Sent</th>
                <th>Job</th>
                <th>Recipient</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center" }}>No notification logs found</td></tr>
              ) : rows.map((log, i) => (
                <tr key={log.id}>
                  <td>{(meta.current_page - 1) * meta.per_page + i + 1}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{formatServerDateTime(log.created_at)}</td>
                  <td>
                    {log.appointment?.appid ? (
                      <Link className="link_preview" to={`/appointments/view/${log.appointment_id}`}>{log.appointment.appid}</Link>
                    ) : "-"}
                  </td>
                  <td className="text-break" style={{ maxWidth: 240 }}>{log.recipient}</td>
                  <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={log.subject || ""}>
                    {log.subject || "(no subject)"}
                  </td>
                  <td>
                    <span style={{
                      display: "inline-block", padding: "1px 10px", fontSize: 12, fontWeight: 600,
                      borderRadius: 10, textTransform: "capitalize", background: "#1c7c3633", color: "#1c7c36",
                    }}>
                      {log.status}
                    </span>
                  </td>
                  <td>
                    <span className="link_preview" style={{ cursor: "pointer" }} onClick={() => setDetailLogId(log.id)}>
                      View
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <Pagination
          totalItems={meta.total}
          currentPage={meta.current_page}
          pageLength={meta.per_page}
          totalPages={meta.last_page}
          handleChange={(page) => fetchLogs(page)}
          prevDisabled={meta.last_page <= 1 || meta.current_page === 1}
          nextDisabled={meta.last_page <= 1 || meta.current_page === meta.last_page}
        />
      </div>

      <NotificationLogDetailModal
        logId={detailLogId}
        isOpen={detailLogId !== null}
        toggle={() => setDetailLogId(null)}
      />
    </div>
  );
};

export default NotificationLogs;
